/**
 * Створення декларації платника єдиного податку (ч.1113): організація
 * (лише єдинники: ФОП 1–3 гр., юрособа 3 гр.), рік і період наростаючим
 * підсумком. Форма (J0103509 / F0103309 / F0103407) обирається сервером
 * з картки організації. Підпис і відправка — у Кабінеті ДПС.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { PERIOD_LABELS, singleTaxDeclarations } from '@/api/singleTaxDeclarations';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useDefaults } from '@/store/defaults';

const SINGLE_TAX = new Set(['FOP_1', 'FOP_2', 'FOP_3_3', 'FOP_3_5', 'SIMPLIFIED_3']);

export default function SingleTaxDeclarationCreatePage() {
  const navigate = useNavigate();
  const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs } =
    usePermissionsByOrg('documents.single_tax_calculation.manage');
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const eligible = availableOrgs.filter((o) => SINGLE_TAX.has(o.tax_system));
  const hidden = availableOrgs.length - eligible.length;

  const now = new Date();
  const prevQ = Math.floor((now.getMonth()) / 3) || 4;
  const prevY = Math.floor(now.getMonth() / 3) === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [orgId, setOrgId] = useState('');
  const [year, setYear] = useState(prevY);
  const [quarter, setQuarter] = useState(prevQ);
  const [reportType, setReportType] = useState<'REPORTING' | 'REPORTING_NEW' | 'CORRECTING'>('REPORTING');
  const [corrYear, setCorrYear] = useState(prevY);
  const [corrQuarter, setCorrQuarter] = useState(prevQ);
  const [error, setError] = useState<string | null>(null);
  const isCorrection = reportType === 'CORRECTING';
  const effectiveOrg = orgId
    || (eligible.some((o) => o.id === defaultOrgId) ? defaultOrgId : '')
    || eligible[0]?.id || '';
  const org = eligible.find((o) => o.id === effectiveOrg);
  const isFop12 = !!org && (org.tax_system === 'FOP_1' || org.tax_system === 'FOP_2');

  const create = useMutation({
    mutationFn: () => singleTaxDeclarations.create({
      organization_id: effectiveOrg, period_year: year,
      period_quarter: isFop12 && !isCorrection ? 4 : quarter,
      report_type: reportType,
      ...(isCorrection ? { corrected_period_year: corrYear, corrected_period_quarter: corrQuarter } : {}),
    }),
    onSuccess: (doc) => navigate(`/taxation/single-tax-declarations/${doc.id}`),
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });

  if (!hasPermissionInAnyOrg) {
    return <div className="card">У вас немає прав створювати декларації у жодній з {totalAccessibleOrgs} організацій.</div>;
  }
  return (
    <div className="card" style={{ maxWidth: 640 }}>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нова декларація єдинника</h2>
        <button style={{ marginLeft: 'auto' }} onClick={() => navigate('/dps-cabinet/new')}>до каталогу</button>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: 0 }}>
        Дохід наростаючим підсумком береться за касовим методом — з проведених надходжень на
        рахунки і в касу (аванси покупців теж дохід; позики, перекази між своїми рахунками,
        повернення і внески до капіталу — ні), ставка і
        форма — з картки організації: юрособа 3 групи — J0103509, ФОП — F0103309 (квартал) або
        F0103407 (рік для 1–2 груп). Річна декларація ФОП іде пакетом з Додатком 1 (ЄСВ «за себе»);
        за наявності земельних ділянок сільгосппризначення — Додаток 2 (мінімальне податкове зобов’язання).
        Підпис і відправка — у Журналі Кабінету ДПС.
      </p>
      {eligible.length === 0 ? (
        <div style={{ color: 'var(--badge-amber-fg)' }}>
          Серед доступних організацій немає платників єдиного податку — перевірте систему
          оподаткування в картці організації.
        </div>
      ) : (
        <>
          <div className="field">
            <label>Організація{hidden > 0 ? ` (не єдинників приховано: ${hidden})` : ''}</label>
            <select value={effectiveOrg} onChange={(e) => setOrgId(e.target.value)}>
              {eligible.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.tax_system}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Тип</label>
            <select value={reportType} onChange={(e) => setReportType(e.target.value as any)}>
              <option value="REPORTING">Звітна</option>
              <option value="REPORTING_NEW">Звітна нова</option>
              <option value="CORRECTING">Уточнююча</option>
            </select>
          </div>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 9' }}>
              <label>{isCorrection ? 'Період подання' : 'Податковий (звітний) період'}</label>
              {isFop12 && !isCorrection
                ? <input value="рік (1–2 групи звітують раз на рік)" disabled />
                : (
                  <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
                    {[1, 2, 3, 4].map((q) => <option key={q} value={q}>{PERIOD_LABELS[q]}</option>)}
                  </select>
                )}
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Рік</label>
              <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
            </div>
          </div>
          {isCorrection && (
            <div className="doc-grid">
              <div className="field" style={{ gridColumn: 'span 9' }}>
                <label>Період, що УТОЧНЮЄТЬСЯ</label>
                <select value={corrQuarter} onChange={(e) => setCorrQuarter(Number(e.target.value))}>
                  {[1, 2, 3, 4].map((q) => <option key={q} value={q}>{PERIOD_LABELS[q]}</option>)}
                </select>
              </div>
              <div className="field" style={{ gridColumn: 'span 3' }}>
                <label>Рік</label>
                <input type="number" value={corrYear} onChange={(e) => setCorrYear(Number(e.target.value))} />
              </div>
            </div>
          )}
          {error && <div style={{ color: 'var(--badge-red-fg)', marginBottom: 8 }}>{error}</div>}
          <button className="primary" disabled={create.isPending || !effectiveOrg} onClick={() => create.mutate()}>
            створити
          </button>
        </>
      )}
    </div>
  );
}
